import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { loadPreferenceFresh, savePreferenceNow } from './useUserPreferences';

/** When this user last opened Supplier Menus — menus shared after this light the red dot. */
export const SUPPLIER_MENUS_SEEN_KEY = 'supplierMenus.lastSeenAt';

/**
 * Supplier Menus red dot. Counts everything new that was shared with the user,
 * whatever path granted it: pending email invitations AND menus newly granted to
 * their company (direct grants, share-link redemption, accepted invitations)
 * since they last opened Supplier Menus.
 */
export const usePendingInvitations = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userEmail = user?.email ?? null;
  const userId = user?.id ?? null;

  const { data: pendingCount = 0, isError } = useQuery({
    queryKey: ['pendingInvitations', userEmail],
    queryFn: async () => {
      if (!userEmail || !userId) return 0;

      const invitesQ = supabase
        .from('menu_invitations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending')
        .ilike('email', userEmail)
        .gt('expires_at', new Date().toISOString());

      const [{ count, error }, lastSeen, { data: me }] = await Promise.all([
        invitesQ,
        loadPreferenceFresh<string>(SUPPLIER_MENUS_SEEN_KEY),
        supabase.from('users').select('company_id').eq('id', userId).maybeSingle(),
      ]);
      /* Never swallow this into "0 pending". A silent zero is indistinguishable from
         genuinely none, and it hides a real invitation the user is waiting on. */
      if (error) throw error;

      let newGrants = 0;
      const companyId = (me as { company_id?: string } | null)?.company_id;
      if (companyId) {
        // First visit ever: only count grants from the last 14 days, not history.
        const since = lastSeen ?? new Date(Date.now() - 14 * 86_400_000).toISOString();
        const { count: grantCount, error: gErr } = await supabase
          .from('company_menu_subscribers')
          .select('id, menu:menus!inner(company_id, is_active)', { count: 'exact', head: true })
          .eq('subscriber_company_id', companyId)
          .is('revoked_at', null)
          .gt('granted_at', since)
          .eq('menu.is_active', true)
          .neq('menu.company_id', companyId);
        if (gErr) throw gErr;
        newGrants = grantCount ?? 0;
      }
      return (count ?? 0) + newGrants;
    },
    enabled: !!userEmail,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!userEmail || !userId) return;
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['pendingInvitations', userEmail] });
    const channel = supabase
      .channel(`invitations:${userId}:${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'menu_invitations' }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'company_menu_subscribers' }, refresh)
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userEmail, userId, queryClient]);

  return { pendingCount, isError };
};

/** Call when the user opens Supplier Menus: newly shared menus are now seen. */
export const useMarkSupplierMenusSeen = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!user?.id) return;
    savePreferenceNow(SUPPLIER_MENUS_SEEN_KEY, new Date().toISOString());
    // Invitations still pending keep the dot; only new grants clear.
    const t = setTimeout(() => queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] }), 1500);
    return () => clearTimeout(t);
  }, [user?.id, queryClient]);
};
